/**
 * Security & Ops Dashboard panel — one-click incident-response actions,
 * lockout management, login forensics, and embedded Product Library.
 *
 * All mutation handlers are passed in from the parent so this component
 * stays purely presentational + drives state machines (busy flags).
 */
import {
  ShieldCheck,
  KeyRound,
  Unlock,
  AlertTriangle,
  Copy as CopyIcon,
  RefreshCw,
  Mail,
  Trash2,
  BellRing,
  Loader2,
} from "lucide-react";
import { ProductLibraryTable } from "./ProductLibraryTable";
import { DeliveryAlertsPanel } from "./DeliveryAlertsPanel";

const ActionCard = ({ title, desc, icon: Icon, buttonLabel, onClick, busy, testid, danger }) => (
  <div className="border border-slate-200 bg-white p-5 flex flex-col" data-testid={`${testid}-card`}>
    <div className="flex items-center gap-2 mb-2">
      <Icon size={16} className={danger ? "text-rose-600" : "text-[#C5A059]"} />
      <span className="font-accent text-[11px] tracking-wider text-[#0A192F]">{title}</span>
    </div>
    <p className="text-xs text-slate-500 leading-relaxed mb-4 flex-1">{desc}</p>
    <button
      data-testid={testid}
      onClick={onClick}
      disabled={busy}
      className={`px-4 py-2.5 text-xs font-accent tracking-wider text-white disabled:opacity-50 ${
        danger ? "bg-rose-600 hover:bg-rose-700" : "bg-[#0A192F] hover:bg-[#0A192F]/90"
      }`}
    >
      {busy ? (
        <span className="inline-flex items-center gap-2">
          <Loader2 size={12} className="animate-spin" /> WORKING…
        </span>
      ) : (
        buttonLabel.toUpperCase()
      )}
    </button>
  </div>
);

export const SecurityOpsPanel = ({
  data,
  products,
  busy,
  generatedSecret,
  deliveryHealth,
  onDismissSecret,
  onReload,
  onReArmReminders,
  onRescueDeliveries,
  onReconcilePayPal,
  onArchivePending,
  onResetBucket,
  onGenerateJwt,
  onTestPager,
  onCopy,
  onEditImage,
  onSaveOrder,
  onToggleVisibility,
  onScheduleProduct,
}) => {
  const failures = data?.failures || [];
  const lockedIps = data?.locked_ips || [];

  return (
    <div data-testid="security-ops-panel" className="space-y-8">
      <DeliveryAlertsPanel
        health={deliveryHealth}
        busy={busy}
        onReload={onReload}
        onRescueNow={onRescueDeliveries}
        onReArmFailed={onReArmReminders}
      />

      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 text-[#0A192F]">
            <ShieldCheck size={18} className="text-[#C5A059]" />
            <h2 className="font-accent text-sm tracking-wider">SECURITY &amp; OPS</h2>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            One-click levers for incident response and login forensics.
            Window: {Math.round((data?.window_seconds || 900) / 60)} min ·
            Max failed attempts: {data?.max_fails ?? 5}
          </p>
        </div>
        <button
          data-testid="security-reload-btn"
          onClick={onReload}
          className="text-xs text-slate-500 hover:text-[#0A192F] flex items-center gap-1"
        >
          <RefreshCw size={12} /> Reload
        </button>
      </div>

      {generatedSecret && (
        <div
          data-testid="generated-jwt-secret-card"
          className="border border-[#C5A059] bg-[#FBF8F1] p-5"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-[#0A192F]">
              <KeyRound size={16} className="text-[#C5A059]" />
              <span className="font-accent text-xs tracking-wider">NEW JWT_SECRET (COPY NOW)</span>
            </div>
            <button
              onClick={onDismissSecret}
              data-testid="dismiss-jwt-secret-btn"
              className="text-xs text-slate-500 hover:text-[#0A192F]"
            >
              Dismiss
            </button>
          </div>
          <div className="bg-white border border-slate-200 p-3 font-mono text-xs text-slate-700 break-all select-all">
            {generatedSecret}
          </div>
          <button
            data-testid="copy-jwt-secret-btn"
            onClick={() => onCopy(generatedSecret, "JWT secret")}
            className="mt-3 inline-flex items-center gap-2 px-3 py-2 bg-[#0A192F] text-white text-xs font-accent hover:bg-[#0A192F]/90"
          >
            <CopyIcon size={12} /> COPY VALUE
          </button>
          <p className="text-[11px] text-slate-600 mt-3 leading-relaxed">
            <strong>To rotate:</strong> Paste into <em>Emergent → App Settings → Environment Variables</em> as <code className="bg-white px-1">JWT_SECRET</code>,
            then redeploy. All existing admin sessions (including yours) will be invalidated.
          </p>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        <ActionCard
          title="RE-ARM FAILED DELIVERIES"
          desc="Re-attempt every reminder MailerSend previously rejected. Sends at 5s throttle with 429 backoff."
          icon={Mail}
          buttonLabel="Re-arm now"
          testid="action-rearm-reminders"
          onClick={onReArmReminders}
          busy={busy === "rearm"}
        />
        <ActionCard
          title="RECONCILE PAYPAL"
          desc="Capture 'initiated' PayPal orders the webhook missed. Idempotent — safe to repeat."
          icon={RefreshCw}
          buttonLabel="Run reconcile"
          testid="action-paypal-reconcile"
          onClick={onReconcilePayPal}
          busy={busy === "paypal"}
        />
        <ActionCard
          title="ARCHIVE PENDING QUEUE"
          desc="Move every pending cart + stuck transaction to archive collections. Stops scheduler nudges."
          icon={Trash2}
          buttonLabel="Archive now"
          testid="action-archive-pending"
          onClick={onArchivePending}
          busy={busy === "archive"}
          danger
        />
        <ActionCard
          title="ROTATE JWT_SECRET"
          desc="Generate a new 64-byte JWT_SECRET. Display-only — paste into env + redeploy to take effect."
          icon={KeyRound}
          buttonLabel="Generate new secret"
          testid="action-rotate-jwt"
          onClick={onGenerateJwt}
          busy={busy === "jwt"}
        />
        <ActionCard
          title="TEST SECURITY PAGER"
          desc="Fire a ✅ test alert to your Discord/Slack webhook so you can confirm the pager is wired without manufacturing a real incident."
          icon={BellRing}
          buttonLabel="Send test alert"
          testid="action-test-pager"
          onClick={onTestPager}
          busy={busy === "test-pager"}
        />
      </div>

      <div className="bg-white border border-slate-200">
        <div className="flex items-center justify-between px-5 py-3 bg-slate-50 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Unlock size={14} className="text-[#C5A059]" />
            <span className="font-accent text-[11px] tracking-wider text-[#0A192F]">
              CURRENTLY LOCKED IPs ({lockedIps.length})
            </span>
          </div>
          {lockedIps.length > 0 && (
            <button
              data-testid="unlock-all-btn"
              onClick={() => onResetBucket(null)}
              disabled={busy === "reset-all"}
              className="text-[11px] font-accent text-rose-600 hover:text-rose-700 disabled:opacity-50"
            >
              {busy === "reset-all" ? "UNLOCKING…" : "UNLOCK ALL"}
            </button>
          )}
        </div>
        {lockedIps.length === 0 ? (
          <div className="px-5 py-8 text-center text-slate-400 text-xs">
            No IPs currently locked out.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-slate-500">
              <tr className="text-left">
                <th className="px-5 py-2 font-accent text-[10px]">IP</th>
                <th className="px-5 py-2 font-accent text-[10px]">FAILS IN WINDOW</th>
                <th className="px-5 py-2 font-accent text-[10px]">RETRY-AFTER</th>
                <th className="px-5 py-2 font-accent text-[10px]">ACTION</th>
              </tr>
            </thead>
            <tbody>
              {lockedIps.map((row) => (
                <tr key={row.ip} className="border-t border-slate-100" data-testid={`locked-ip-${row.ip}`}>
                  <td className="px-5 py-3 font-mono text-xs text-[#0A192F]">{row.ip}</td>
                  <td className="px-5 py-3 text-slate-600">{row.fails_in_window}</td>
                  <td className="px-5 py-3 text-slate-600">{Math.ceil(row.retry_after_seconds / 60)} min</td>
                  <td className="px-5 py-3">
                    <button
                      data-testid={`unlock-ip-btn-${row.ip}`}
                      onClick={() => onResetBucket(row.ip)}
                      disabled={busy === `reset-${row.ip}`}
                      className="px-3 py-1.5 bg-[#0A192F] text-white text-[11px] font-accent hover:bg-[#0A192F]/90 disabled:opacity-50"
                    >
                      {busy === `reset-${row.ip}` ? "UNLOCKING…" : "UNLOCK"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="bg-white border border-slate-200">
        <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
          <AlertTriangle size={14} className="text-[#C5A059]" />
          <span className="font-accent text-[11px] tracking-wider text-[#0A192F]">
            RECENT FAILED LOGIN ATTEMPTS (LAST {failures.length})
          </span>
        </div>
        {failures.length === 0 ? (
          <div className="px-5 py-8 text-center text-slate-400 text-xs">
            No failed login attempts on record. (In-memory buffer resets on backend restart.)
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-slate-500">
              <tr className="text-left">
                <th className="px-5 py-2 font-accent text-[10px]">WHEN</th>
                <th className="px-5 py-2 font-accent text-[10px]">IP</th>
                <th className="px-5 py-2 font-accent text-[10px]">EMAIL (MASKED)</th>
                <th className="px-5 py-2 font-accent text-[10px] text-right">FAIL # IN WINDOW</th>
              </tr>
            </thead>
            <tbody>
              {failures.map((f, i) => (
                <tr key={`${f.ts}-${i}`} className="border-t border-slate-100" data-testid={`failed-login-row-${i}`}>
                  <td className="px-5 py-2.5 text-slate-600">{new Date(f.ts).toLocaleString()}</td>
                  <td className="px-5 py-2.5 font-mono text-xs text-[#0A192F]">{f.ip}</td>
                  <td className="px-5 py-2.5 text-slate-600">{f.email_masked}</td>
                  <td className="px-5 py-2.5 text-slate-600 text-right">{f.fails_in_window}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Product Library — inline image management + drag-to-reorder */}
      <ProductLibraryTable
        products={products || []}
        onEditImage={onEditImage}
        onSaveOrder={onSaveOrder}
        onToggleVisibility={onToggleVisibility}
        onScheduleProduct={onScheduleProduct}
      />
    </div>
  );
};
